import { axiosFetch as authFetch } from './axiosFetch';
/**
 * Loyalty Members API Service (APIs 18-22)
 * Base URL: REACT_APP_API_ENDPOINT_LOYALTY
 * All endpoints: /api/v1/loyalty/members/* and /api/v1/loyalty/export
 */

import type { Member, PointActivity } from '../components/loyalty/data/types';
import { createRequest, getUserId } from '../utils/hmac-utils';

const LOYALTY_BASE = process.env.REACT_APP_API_ENDPOINT_LOYALTY || 'http://localhost:4004';

function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  try {
    const stored = localStorage.getItem('CREDENTIALS');
    if (stored) {
      const creds = JSON.parse(stored);
      const token = creds?.accessToken || creds?.access_token;
      if (token && typeof token === 'string' && token.trim()) {
        headers['Authorization'] = `Bearer ${token.trim()}`;
      }
      const merchantId = creds?.merchantId;
      if (merchantId && typeof merchantId === 'string' && merchantId.trim()) {
        headers['x-brand-id'] = merchantId.trim();
      }
    }
  } catch {
    // ignore parse errors
  }
  return headers;
}

async function handleResponse<T = any>(response: Response): Promise<T> {
  if (response.status === 401) {
    throw new Error('Session expired. Please log in again.');
  }
  const text = await response.text();
  let json: any;
  try {
    json = JSON.parse(text);
  } catch {
    throw new Error(`Invalid JSON response: ${text.substring(0, 200)}`);
  }
  if (!response.ok) {
    const errorMessage = json?.error?.message || json?.message || json?.error || `Request failed with status ${response.status}`;
    throw new Error(errorMessage);
  }
  if (json.success === false) {
    const errorMessage = json?.error?.message || json?.message || 'Request failed';
    throw new Error(errorMessage);
  }
  return json;
}

export async function requestJsonLoyalty(
  url: string,
  body: unknown,
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
): Promise<unknown> {
  const headers = { ...getAuthHeaders(), 'X-source': 'MP', Accept: 'application/json' };
  const userId = getUserId();
  const bodyWithUser = userId ? { ...(body as object), staff_id: userId } : body;
  const requestPayload = await createRequest(bodyWithUser);

  const res = await fetch(url, {
    method,
    headers,
    body: JSON.stringify(requestPayload),
  });
  return handleResponse(res);
}

// ─── API Response Types ─────────────────────────────────────────

export interface ApiMemberTier {
  tier_id: string;
  tier_name: string;
  tier_level: number;
}

export interface ApiMemberListItem {
  id: string;
  customer_id: string;
  full_name?: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  points_balance: number;
  lifetime_points_earned: number;
  lifetime_points_redeemed: number;
  current_tier_id: string | null;
  total_visits: number;
  total_spend: number | string;
  enrolled_at: string;
  last_visit_at: string | null;
  enrollment_channel: string | null;
  // DISABLED: status: 'active' | 'at_risk' | 'lapsed' | 'lost';
  status?: string;
}

export interface ApiNextTier {
  tier_id: string;
  tier_name: string;
  tier_level: number;
  spend_required: number;
  spend_remaining: number;
}

export interface ApiCurrentTierDetail {
  tier_id: string;
  tier_name: string;
  tier_level: number;
  qualified_at: string;
  qualification_method: string;
  qualification_value: number;
  next_tier: ApiNextTier | null;
}

export interface ApiTierHistoryEntry {
  tier_id: string;
  tier_name: string;
  action: string;
  effective_date: string;
  reason: string;
}

export interface ApiActivitySummary {
  total_visits: number;
  total_spend: number;
  average_order_value: number;
  total_rewards_redeemed: number;
  last_visit_at: string | null;
  last_visit_location: string;
  favorite_location: string;
  visits_this_month: number;
  spend_this_month: number;
}

export interface ApiRecentTransaction {
  transaction_id: string;
  type: string;
  points: number;
  description: string;
  order_total?: number;
  reward_name?: string;
  created_at: string;
}

export interface ApiMemberProfile {
  id?: string;
  loyalty_customer_id?: string;
  customer_id: string;
  program_id: string;
  full_name?: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  date_of_birth: string | null;
  enrollment_source?: string;
  enrollment_channel?: string;
  status: 'active' | 'inactive' | 'suspended';
  points_balance: number;
  lifetime_points_earned: number;
  lifetime_points_redeemed: number;
  points_expiring_soon?: { amount: number; expiration_date: string } | null;
  current_tier: ApiCurrentTierDetail | null;
  tier_history: ApiTierHistoryEntry[];
  activity_summary?: ApiActivitySummary;
  recent_transactions: ApiRecentTransaction[];
  transactions?: ApiTransaction[];
  enrolled_at: string;
  updated_at: string;
  last_visit_at?: string | null;
  total_visits?: number;
  notification_enabled?: boolean;
}

export interface ApiTransaction {
  transaction_id: string;
  type: string;
  points: number;
  balance_after: number;
  description: string;
  adjusted_by: string | null;
  location_id: string | null;
  location_name: string | null;
  order_id: string | null;
  transaction_type?: string | null;
  reward_id?: string;
  reward_name?: string;
  adjustment_reason?: string;
  created_at: string;
}

export interface ApiPagination {
  page: number;
  limit: number;
  total_items: number;
  total_pages: number;
}

// â”€â”€â”€ Request Types â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

export interface ListMembersParams {
  page?: number;
  limit?: number;
  search?: string;
  tier_id?: string;
  status?: string;
  enrolled_after?: string;
  enrolled_before?: string;
  min_points?: number;
  max_points?: number;
  sort?: string;
  order?: 'asc' | 'desc';
  channel?: 'pos' | 'online' | 'gfd' | 'kiosk';
}

export interface AdjustPointsPayload {
  points: number;
  reason: string;
  reference_order_id?: string;
  notify_customer?: boolean;
}

export interface AdjustPointsResponse {
  success: boolean;
  data: {
    transaction_id: string;
    loyalty_customer_id: string;
    adjustment_type: 'credit' | 'debit';
    points: number;
    previous_balance: number;
    new_balance: number;
    reason: string;
    reference_order_id: string | null;
    adjusted_by: string;
    notify_customer: boolean;
    created_at: string;
  };
}

export interface ExportPayload {
  export_type: 'members' | 'transactions' | 'redemptions' | 'summary_report';
  format: 'csv' | 'xlsx';
  filters?: {
    status?: string;
    tier_id?: string | null;
    enrolled_after?: string;
    enrolled_before?: string | null;
    min_points?: number;
    search?: string;
    channel?: string;
  };
  fields?: string[];
  date_range?: {
    from: string;
    to: string;
  };
}

export interface ExportResponse {
  success: boolean;
  data: {
    export_id: string;
    export_type: string;
    format: string;
    status: string;
    estimated_records: number;
    requested_at: string;
    download_url: string | null;
    expires_at: string | null;
  };
}

// â”€â”€â”€ Mapper Functions â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

// DISABLED: mapStatusToEngagement and mapEngagementToApiStatus — lapsed/status concept hidden from UI
// function mapStatusToEngagement(status: string): Member['engagementStatus'] {
//   switch (status) {
//     case 'active': return 'active';
//     case 'at_risk': return 'at-risk';
//     case 'lapsed': return 'lapsed';
//     case 'lost': return 'lapsed';
//     default: return 'active';
//   }
// }

// function mapEngagementToApiStatus(engagement: string): string {
//   switch (engagement) {
//     case 'active': return 'active';
//     case 'at-risk': return 'at_risk';
//     case 'lapsed': return 'lapsed';
//     default: return engagement;
//   }
// }

export function mapEngagementToApiStatus(_engagement: string): string {
  return _engagement; // DISABLED: status filtering removed from UI
}

function mapTransactionType(apiType: string): PointActivity['type'] {
  // Backend has been observed sending both underscore ("adjust_credit") and
  // space-separated ("adjust credit") forms for the same transaction type —
  // normalize to underscore so either form hits the matching case below.
  const normalized = (apiType || '').toLowerCase().trim().replace(/\s+/g, '_');
  switch (normalized) {
    case 'earn': return 'earned';
    case 'redeem': return 'redeemed';
    case 'adjust_credit': return 'adjusted';
    case 'adjust_debit': return 'adjusted';
    case 'expire': return 'reversed';
    case 'bonus': return 'bonus-signup';
    case 'sign_up': return 'bonus-signup';
    case 'birthday': return 'bonus-birthday';
    case 'promotion': return 'earned';
    case 'refund': return 'reversed';
    default: return 'adjusted';
  }
}

export function mapApiMemberToMember(api: ApiMemberListItem): Member {
  return {
    id: api.id,
    fullName: api.full_name || [api.first_name, api.last_name].filter(Boolean).join(' ') || 'N/A',
    firstName: api.first_name,
    lastName: api.last_name,
    phone: api.phone,
    email: api.email,
    birthday: null,
    currentBalance: api.points_balance,
    lifetimeEarned: api.lifetime_points_earned,
    lifetimeRedeemed: api.lifetime_points_redeemed,
    totalVisits: api.total_visits,
    lastVisitDate: api.last_visit_at,
    enrollmentDate: api.enrolled_at,
    enrollmentChannel: (api.enrollment_channel as Member['enrollmentChannel']) || 'pos',
    // DISABLED: engagementStatus: mapStatusToEngagement(api.status),
    notes: '',
  };
}

export function mapApiProfileToMember(api: ApiMemberProfile): Member {
  return {
    id: api.id ?? api.loyalty_customer_id ?? '',
    fullName: api.full_name || [api.first_name, api.last_name].filter(Boolean).join(' ') || 'N/A',
    firstName: api.first_name,
    lastName: api.last_name,
    phone: api.phone,
    email: api.email,
    birthday: api.date_of_birth,
    currentBalance: api.points_balance,
    lifetimeEarned: api.lifetime_points_earned,
    lifetimeRedeemed: api.lifetime_points_redeemed,
    totalVisits: api.total_visits ?? api.activity_summary?.total_visits ?? 0,
    lastVisitDate: api.last_visit_at ?? api.activity_summary?.last_visit_at ?? null,
    enrollmentDate: api.enrolled_at,
    enrollmentChannel: ((api.enrollment_channel ?? api.enrollment_source) as Member['enrollmentChannel']) || 'pos',
    // DISABLED: engagementStatus: mapStatusToEngagement(api.status),
    notes: '',
    notification_enabled: api.notification_enabled ?? undefined,
  };
}

export function mapApiTransactionToActivity(transaction: ApiTransaction, memberId: string): PointActivity {
  return {
    id: transaction.transaction_id,
    memberId,
    date: transaction.created_at,
    type: mapTransactionType(transaction.type),
    points: transaction.points,
    runningBalance: transaction.balance_after,
    orderId: transaction.order_id,
    transactionType: transaction.transaction_type ?? null,
    locationId: transaction.location_id || '',
    locationName: transaction.location_name || '',
    channel: transaction.adjusted_by ? 'admin' : 'pos',
    adminName: transaction.adjusted_by || undefined,
    reason: transaction.description || undefined,
    adjustmentReason: transaction.adjustment_reason || undefined,
  };
}

// â”€â”€â”€ API 18: List Loyalty Members â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export const listMembers = async (params?: ListMembersParams): Promise<{ data: { members: ApiMemberListItem[]; pagination: ApiPagination } }> => {
  const query = new URLSearchParams();
  if (params?.page) query.set('page', String(params.page));
  if (params?.limit) query.set('limit', String(params.limit));
  if (params?.search) query.set('search', params.search);
  if (params?.tier_id) query.set('tier_id', params.tier_id);
  if (params?.status) query.set('status', params.status);
  if (params?.enrolled_after) query.set('enrolled_after', params.enrolled_after);
  if (params?.enrolled_before) query.set('enrolled_before', params.enrolled_before);
  if (params?.min_points !== undefined) query.set('min_points', String(params.min_points));
  if (params?.max_points !== undefined) query.set('max_points', String(params.max_points));
  if (params?.sort) query.set('sort', params.sort);
  if (params?.order) query.set('order', params.order);
  if (params?.channel) query.set('channel', params.channel);
  const qs = query.toString();
  const response = await fetch(`${LOYALTY_BASE}/api/v1/loyalty/members${qs ? `?${qs}` : ''}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

// â”€â”€â”€ API 19: Get Member Profile â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export const getMemberProfile = async (loyaltyCustomerId: string): Promise<{ data: ApiMemberProfile }> => {
  const response = await fetch(`${LOYALTY_BASE}/api/v1/loyalty/members/${loyaltyCustomerId}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

// â”€â”€â”€ API 20: Adjust Member Points â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export const adjustMemberPoints = async (loyaltyCustomerId: string, payload: AdjustPointsPayload): Promise<AdjustPointsResponse> => {
  return requestJsonLoyalty(
    `${LOYALTY_BASE}/api/v1/loyalty/members/${loyaltyCustomerId}/points/adjust`,
    payload,
    'POST',
  ) as Promise<AdjustPointsResponse>;
};

// ─── API: Update Member Notification ───────────────────────────
export const updateMemberNotification = async (
  loyaltyCustomerId: string,
  notification_enabled: boolean,
): Promise<{ success: boolean }> => {
  return requestJsonLoyalty(
    `${LOYALTY_BASE}/api/v1/loyalty/members/${loyaltyCustomerId}/notification`,
    { notification_enabled },
    'PUT',
  ) as Promise<{ success: boolean }>;
};

// ─── API 22: Export Members (returns binary CSV stream) ──────────
export const exportMembers = async (payload: ExportPayload): Promise<Blob> => {
  const headers = { ...getAuthHeaders(), 'X-source': 'MP', Accept: 'text/csv,application/octet-stream' };
  const userId = getUserId();
  const bodyWithUser = userId ? { ...(payload as object), userId, staff_id: userId } : payload;
  const requestPayload = await createRequest(bodyWithUser);
  const res = await fetch(`${LOYALTY_BASE}/api/v1/loyalty/export`, {
    method: 'POST',
    headers,
    body: JSON.stringify(requestPayload),
  });
  if (res.status === 401) throw new Error('Session expired. Please log in again.');
  if (!res.ok) throw new Error(`Export failed with status ${res.status}`);

  const text = await res.text();
  try {
    // Backend is serialising NestJS StreamableFile as JSON instead of piping the stream.
    // Extract the raw CSV bytes from the internal buffer until the backend is fixed.
    const json = JSON.parse(text);
    const bufferData: number[] | undefined =
      json?.data?.stream?._readableState?.buffer?.[0]?.data;
    if (Array.isArray(bufferData)) {
      return new Blob([new Uint8Array(bufferData)], { type: 'text/csv' });
    }
  } catch {
    // Not JSON — response is a proper raw CSV stream, use it directly
  }
  return new Blob([text], { type: 'text/csv' });
};

// â”€â”€â”€ API 22b: Get Export Status â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export const getExportStatus = async (exportId: string): Promise<ExportResponse> => {
  const response = await fetch(`${LOYALTY_BASE}/api/v1/loyalty/export/${exportId}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleResponse<ExportResponse>(response);
};

// Poll export until status is 'completed' or 'failed', then return download_url
export const pollExportUntilReady = async (exportId: string, intervalMs = 2000, maxAttempts = 15): Promise<string> => {
  for (let i = 0; i < maxAttempts; i++) {
    await new Promise((res) => setTimeout(res, intervalMs));
    const result = await getExportStatus(exportId);
    const { status, download_url } = result.data;
    if (status === 'completed' && download_url) return download_url;
    if (status === 'failed') throw new Error('Export failed on the server. Please try again.');
  }
  throw new Error('Export is taking too long. Please try again later.');
};

// DISABLED: Re-export mapper utility for status conversion in UI filters
// export { mapEngagementToApiStatus };
